<template>
    <section class="notifications-admin-edit-modal">
        <XModal v-show="visible" @ok="save" @cancel="close" :wWidth="1000" :resizable="true" okText="Save"
                :loading="loading">
            <div slot="title">Create Ticket</div>

            <section>
                <el-form ref="form" :model="form" :rules="rules" label-width="120px" label-position="top">
                    <el-form-item
                        label="Tenants, Teams & Brokers"
                        prop="tenantsTeamsAndBrokers"
                        v-if="!_.isEmpty(tenantsTeamsBrokers) && !isTenant"
                    >
                        <el-cascader
                            v-model="form.tenantsTeamsBrokers"
                            :options="filters.tenantsTeamsBrokers"
                            collapse-tags
                            :props="{ checkStrictly: true }"
                            clearable
                            filterable
                            :filter-method="(n, k) => n.getText(false, null).toLowerCase().indexOf(k.toLowerCase()) >= 0"
                            popper-class="custom-cascader"
                            :style="{ width: '100%' }"
                            class="fin-capitalize"
                        ></el-cascader>
                    </el-form-item>
                    <el-form-item label="CCs" prop="CCs" v-if="!isTenant">
                        <el-select
                            class="fin-w-full"
                            v-model="form.ccs"
                            multiple
                            filterable
                            remote
                            reserve-keyword
                            placeholder="Please enter a keyword"
                            :remote-method="remoteMethod"
                            :loading="loading"
                        >
                            <el-option
                                v-for="(item, key) in ccsOptions"
                                :key="key"
                                :label="item.first_name ? item.first_name + ' ' + item.last_name : item.email"
                                :value="item.email"
                            >
                                <div class="fin-flex fin-justify-between fin-items-center">
                                    <p>{{ item.first_name + " " + item.last_name }}</p>
                                    <p class="fin-font-semibold">{{ item.email }}</p>
                                </div>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Requester" prop="requester" v-if="!isTenant">
                        <el-select class="w-100" v-model="requester" clearable>
                            <el-option
                                v-for="(request, key) in requesters"
                                :key="request.id"
                                :label="_.get(request, 'full_name')"
                                :value="request.id"
                            >
                                <div class="fin-flex fin-justify-between fin-items-center">
                                    <p>{{ _.get(request, "full_name") }}</p>
                                    <p class="fin-font-semibold">{{ _.get(request, "email") }}</p>
                                </div>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Assingee" prop="assignee" v-if="!isTenant">
                        <el-select class="w-100" v-model="form.assignee" clearable>
                            <el-option
                                v-for="(assignee, key) in assignees"
                                :key="assignee.id"
                                :label="_.get(assignee, 'full_name')"
                                :value="assignee.id"
                            ></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Priority" prop="priority" v-if="!isTenant">
                        <el-select class="w-100" v-model="form.priority" clearable>
                            <el-option
                                v-for="(priorityOption, key) in priorityOptions"
                                :key="key"
                                :label="priorityOption"
                                :value="key"
                            ></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Category" prop="category">
                        <el-select class="w-100" v-model="form.category" clearable>
                            <el-option
                                v-for="(categoryOption, key) in mergedCategoryOptions()"
                                :key="key"
                                :label="categoryOption"
                                :value="categoryOption"
                            ></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Subject" prop="subject">
                        <el-input v-model="form.subject"></el-input>
                    </el-form-item>
                    <el-form-item label="Content" prop="body">
                        <label class="fin-block fin-mb-2 fin-text-sm fin-font-medium fin-text-gray-900"
                               for="ticket_multiple_files">
                            Include Attachments ?
                        </label>

                        <el-upload
                            class="upload-demo fin-mt-3"
                            action="https://jsonplaceholder.typicode.com/posts/"
                            :on-change="onChangeAttachments"
                            :on-remove="removeChangeAttachments"
                            :file-list="files"
                            :auto-upload="false"
                            multiple>
                            <el-button size="small" type="primary">Click to upload</el-button>

                            <div slot="tip" class="el-upload__tip">
                                JPG, JPEG, PNG, CSV, XLX, XLS, or PDF (MAX. 2048).
                            </div>
                        </el-upload>

                        <Quill v-if="visible" v-model.sync="form.body"></Quill>
                    </el-form-item>
                </el-form>
            </section>
        </XModal>
    </section>
</template>

<script>
import XModal from "@shared/components/modal/Modal.vue"
import Quill from "@shared/components/TextEditor/Quill"
import moment from "moment"

export default {
    name: "CreateTicket",
    components: {
        XModal,
        Quill,
    },
    props: {
        visible: {type: Boolean, default: false},
        tenantsTeamsBrokers: {
            type: [Array],
            default: null,
            required: false,
        },
        priorityOptions: {
            required: true,
            type: [Array, Object],
        },
        categoryOptions: {
            required: true,
            type: [Array, Object],
        },
        adminCategoryOptions: {
            required: false,
            type: [Array, Object],
        },
        applicationId: {
            required: false,
            default: null,
            type: [String],
        },
        authUser: {
            required: false,
            default: null,
            type: [Object],
        },
        tenant: {
            required: false,
            default: null,
        },
        currentTeamId: {
            required: false,
            default: null,
        },
        isTenant: {
            required: false,
            default: false,
        },
        hasPermissions: {
            required: false,
            default: false,
        },
        masterHasPermissions: {
            required: false,
            default: false,
        }
    },
    data() {
        return {
            form: {},
            rules: {
                category: [{required: true, message: "Category is required"}],
                subject: [{required: true, message: "Subject is required"}],
                content: [{required: true, message: "Content is required"}],
            },
            assignees: [],
            loading: false,
            selectedAssignee: null,
            modalOpen: false,
            ccsLists: [],
            ccsOptions: [],
            requesters: [],
            requester: null,
            selectedRequester: null,
            files: [],
            attachments: [],
        }
    },
    computed: {
        filters() {
            if (this.tenantsTeamsBrokers) {
                const data = {
                    tenantsTeamsBrokers: this.tenantsTeamsBrokers.map((tenantsTeamsBroker) => {
                        let children = tenantsTeamsBroker.teams.map((team) => {
                            const children = team.users.map((user) => ({
                                value: user.id,
                                label: _.get(user, "first_name") + " " + _.get(user, "last_name"),
                            }))

                            return {value: team.id, label: team.name, children}
                        })
                        if (children.length === 0) children = [{value: "All Teams", label: "All Teams"}]
                        return {value: tenantsTeamsBroker.id, label: tenantsTeamsBroker.name, children}
                    }),
                }

                // Handle single selects, if selected they should clear all other selections and mark other options as disabled
                Object.keys(data).forEach((key) => {
                    data[key]
                        .filter((e) => e.singleSelect)
                        .forEach((e) => {
                            const value = e.value
                            const isSelected = _.get(this.form, key, []).includes(value)
                            if (isSelected) {
                                // Clear other selections
                                this.form[key] = this.form[key].filter((e) => e === value)
                                // Disable other options
                                data[key] = data[key].filter((e) =>
                                    Object.assign(e, {disabled: e.value === value ? false : true})
                                )
                            }
                        })
                })

                return data
            } else {
                return {}
            }
        },
        niceTenantsAndTeamsList() {
            const list = this.form.tenantsTeamsBrokers
            if (!Array.isArray(list)) return false

            const tenantsAndTeams = {}

            list.forEach((e) => {
                const tenant = e[0]
                const team = e[1]
                if (Array.isArray(tenantsAndTeams[tenant])) tenantsAndTeams[tenant].push(team)
                else tenantsAndTeams[tenant] = [team]
            })

            const tenantsAndTeamsArray = Object.entries(tenantsAndTeams).map((e) => {
                const tenant = e[0]
                const teamsList = e[1].join(", ")

                const filter = this.filters.tenantsTeamsBrokers.find((e) => e.value === tenant)
                const areAllTeamsSelected = filter.children ? filter.children.length === e[1].length : false

                return `${tenant}${areAllTeamsSelected ? `` : ` (${teamsList})`}`
            })

            const niceList = this.niceList(tenantsAndTeamsArray)

            return niceList
        },
        isApi() {
            return this.isTenant ? this.$tenant : this.$admin
        }
    },
    watch: {
        "form.assignee": {
            handler(val) {
                if (val) {
                    this.selectedAssignee = _.find(this.assignees, {id: val})
                }
            },
            immediate: true,
            deep: true,
        },
        "requester": {
            handler(val) {
                if (val) {
                    this.selectedRequester = _.find(this.requesters, {id: val})
                }
            },
            immediate: true,
            deep: true,
        },
        "form.tenantsTeamsBrokers": {
            handler(val) {
                if (val) {
                    this.getRequesters(_.get(val, 0))
                }
            },
            immediate: true,
            deep: true,
        },
        visible: {
            handler(val) {
                if (val) {
                    if (!this.isTenant) {
                        this.getAssignees()
                        this.getRequesters()
                    }
                }
            },
            immediate: true,
            deep: true,
        },
        isTenant: {
            handler(val) {
                if (!val) {
                    const newRules = {assignee: [{required: true, message: "Assignee is required"}]}
                    this.rules = {...this.rules, ...newRules}
                }
            },
            immediate: true,
            deep: true,
        },
    },
    mounted() {
    },
    beforeDestroy() {
    },
    updated() {
    },
    activated() {
    },
    methods: {
        mergedCategoryOptions() {
            if (this.hasPermissions || this.masterHasPermissions) {
                return _.merge(this.categoryOptions, this.adminCategoryOptions)
            } else {
                return this.categoryOptions
            }
        },
        remoteMethod(query) {
            if (query !== "") {
                this.loading = true
                setTimeout(() => {
                    this.loading = false

                    this.isApi
                        .get(`/api/tickets/search-ticket-ccs`, {
                            params: {
                                ccsQuery: query,
                                tenant: _.get(this.form, "tenantsTeamsBrokers.0", Ambition.tenantId()),
                                team: _.get(this.form, "tenantsTeamsBrokers.1"),
                            },
                        })
                        .then((res) => {
                            this.ccsOptions = res.data
                        })
                        .catch((error) => {
                            console.log("getTenantTeamBrokers error ", error)
                        })
                }, 200)
            } else {
                this.ccsOptions = []
            }
        },
        close() {
            this.$emit("update:visible", false)
            this.form = {}
            this.$bus.$emit("reload-tickets")
            this.$bus.$emit("close-create-modal")
        },
        save() {
            this.$refs.form.validate(async (valid, errors) => {
                if (valid) {
                    this.loading = true

                    this.isApi
                        .post(`/api/tickets`, {
                            ...this.form,
                            selectedAssignee: this.selectedAssignee,
                            selectedRequester: this.selectedRequester,
                            applicationId: this.applicationId,
                            authUser: this.authUser,
                            tenant: this.tenant,
                            currentTeamId: this.currentTeamId,
                            isTenant: this.isTenant,
                        })
                        .then((res) => {
                            this.$message({
                                message: "Ticket Created Successfully",
                                type: "success",
                            })

                            this.uploadAttachments(_.get(res, 'data'))

                            this.$bus.$emit("reload-tickets")

                            this.loading = false
                            this.close()
                        })
                        .catch((error) => {
                            this.$message({
                                message: "Error Creating Ticket",
                                type: "error",
                            })

                            this.loading = false
                        })
                }
            })
        },
        uploadAttachments(ticket) {
            let formData = null

            if (_.size(this.attachments)) {
                formData = new FormData();

                for (let i = 0; i < this.attachments.length; i++) {
                    formData.append('files[]', this.attachments[i])
                }

                this.isApi.post(`/api/tickets/${_.get(ticket, 'id')}/save-ticket-attachments`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    }
                }).then((res) => {
                    this.files = []
                    $("#ticket_multiple_files").val('');
                }).catch(error => {
                    this.files = []
                    $("#ticket_multiple_files").val('');

                    this.$message({
                        message: `${_.get(error, 'message')}`,
                        type: "error",
                    })
                })
            }
        },
        getAssignees() {
            this.isApi
                .get(`/api/tickets/support-developer-brokers`)
                .then((res) => {
                    this.assignees = res.data
                })
                .catch((error) => {
                    console.log("getAssingees error ", error)
                })
        },
        getRequesters(tenant = null) {
            this.isApi
                .get(`/api/tickets/get-requester`, {
                    params: {
                        tenant: tenant ? tenant : _.get(this.form.tenantsTeamsBrokers, "0"),
                    },
                })
                .then((res) => {
                    this.requesters = res.data
                })
                .catch((error) => {
                    console.log("getRequesters error ", error)
                })
        },
        niceList(list, joiner = "and", titleCase = false) {
            list = list.slice(0).map((e) => (titleCase ? _.startCase(e) : e))
            // console.log("niceList", list)
            if (!Array.isArray(list)) return undefined
            if (list.length === 0) return undefined
            if (list.length === 1) return list.join(", ")
            const lastItem = list.pop()
            return `${list.join(", ")} ${joiner} ${lastItem}`
        },
        onChangeAttachments(event) {
            this.attachments.push(_.get(event, 'raw'));
        },
        removeChangeAttachments(event) {
            _.remove(this.attachments, function (attachments) {
                return attachments.uid !== _.get(event, 'raw.uid')
            })
        },
    },
}
</script>

<style lang="scss" scoped>
// @import '@assets/sass/backend/_core-variables.scss';

.el-form-item__label {
    color: red !important;
}
</style>

<style lang="scss">
.notifications-admin-edit-modal {
    .input-border {
        border: 1px solid #dcdfe6;
    }

    .el-form-item__label {
        margin-bottom: 0;
        padding-bottom: 0;
    }

    .modal {
        // overflow: visible;
    }

    .modal-body {
        // overflow: visible !important;
    }

    .el-alert__description {
        margin-top: 0;
    }

    .ql-tooltip.ql-editing {
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
    }
}

.custom-cascader.el-popper.el-cascader__dropdown {
    z-index: 10000 !important;
}
</style>
